import React from 'react';
import { useSelector } from 'react-redux';

interface ButtonProps {
    variant?: keyof typeof ButtonVariant;
    children: React.ReactNode;
    className?: string;
    onClick?: (() => void) | ((e: React.MouseEvent<HTMLButtonElement>) => void);
    onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    tooltipText?: string;
    elementRef?: any;
    disabled?: boolean;
}

enum ButtonVariant {
    primary = 'primary',
    secondary = 'secondary',
    disabled = 'disabled',
    primaryOutlined = 'primaryOutlined',
    secondaryOutlined = 'secondaryOutlined',
    disabledOutlined = 'disabledOutlined',
    primaryRound = 'primaryRound',
    secondaryRound = 'secondaryRound',
    transparentRound = 'transparentRound',
    disabledRound = 'disabledRound',
    grayRound = 'grayRound',
    primaryBigRound = 'primaryBigRound',
    secondaryBigRound = 'secondaryBigRound',
    transparentBigRound = 'transparentBigRound',
}

export function Button({
    onClick,
    onMouseDown,
    children,
    variant = 'primary',
    className = '',
    tooltipText = '',
    elementRef = null,
    disabled = false,
}: ButtonProps) {
    const baseStyles: string = 'leading-[25px] transition duration-200 ease-in-out font-normal focus:outline-none';
    const sizeStyles = 'py-1 md:py-3 px-4 md:px-6 text-body rounded-button-border-radius';
    const roundStyles =
        'h-[25px] md:h-[35px] w-[25px] md:w-[35px] text-body !p-0 !m-0 flex justify-center items-center  rounded-full';
    const bigRoundStyles = 'h-[50px] w-[50px] !p-0 !m-0 flex justify-center items-center text-[21px] rounded-full';
    const currentThemeColor = useSelector((state: any) => state.themes.currentThemeColor);

    const variants = {
        primary: `${sizeStyles} bg-button-primary-background text-layout-textColorOnPrimary hover:bg-button-primary-background-hover`,
        secondary: `${sizeStyles} group-box:hover:bg-button-secondary-box-background-hover bg-button-secondary-background text-button-secondary-text hover:bg-button-secondary-background-hover hover:text-button-secondary-text-hover`,
        disabled: `${sizeStyles} bg-button-disabled-background text-button-disabled-text cursor-not-allowed`,
        primaryOutlined: `${sizeStyles} border-2 border-button-primary-border bg-button-primary-outline-background text-button-primary-outline-text hover:bg-button-primary-outline-background-hover hover:text-button-primary-outline-text-hover`,
        secondaryOutlined: `${sizeStyles} border-2 border-button-secondary-border bg-button-secondary-outline-background text-button-secondary-outline-text hover:bg-button-secondary-outline-background-hover hover:text-button-secondary-outline-text-hover`,
        disabledOutlined: `${sizeStyles} border-2 border-button-disabled-border text-button-disabled-outline-text cursor-not-allowed`,
        primaryRound: `${roundStyles} bg-button-primary-background text-layout-textColorOnPrimary hover:bg-button-primary-background-hover`,
        secondaryRound: `${roundStyles} bg-button-secondary-background group-box:bg-button-secondary-box-background text-button-secondary-text hover:bg-button-secondary-background-hover group-box:hover:bg-button-secondary-box-background-hover hover:text-button-secondary-text-hover`,
        transparentRound: `${roundStyles} bg-transparent text-button-transparent-text hover:text-button-transparent-text-hover`,
        grayRound: `${roundStyles} bg-gray-600 text-white hover:bg-secondary-hover`,
        disabledRound: `${roundStyles} bg-button-disabled-background text-button-disabled-text cursor-not-allowed`,
        primaryBigRound: `${bigRoundStyles} bg-button-primary-background text-layout-textColorOnPrimary hover:bg-button-primary-background-hover`,
        secondaryBigRound: `${bigRoundStyles} bg-button-secondary-background text-button-secondary-text hover:bg-button-secondary-background-hover hover:text-button-secondary-text-hover`,
        transparentBigRound: `${bigRoundStyles} bg-transparent ${
            currentThemeColor === 'light' ? 'hover:bg-[rgba(0,0,0,0.1)]' : 'hover:bg-[rgba(255,255,255,0.2)]'
        }   text-layout-textColorOnPrimary `,
    };

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${tooltipText ? 'tooltip' : ''} ${className}`}
            onClick={(e) => onClick && onClick(e)}
            onMouseDown={(e) => onMouseDown && onMouseDown(e)}
            {...(tooltipText && { 'data-tooltip-content': tooltipText })}
            ref={elementRef}
            disabled={disabled}
        >
            {children}
        </button>
    );
}
